import React, { memo } from 'react';
import { AppNode } from '@/types';
import { Image as ImageIcon2 } from 'lucide-react';
import { GridOverlay } from '../GridOverlay';
import { VideoProgressBar } from './VideoProgressBar';
import { RetryImage } from './RetryImage';

export const MediaUploadContent: React.FC<{
  data: AppNode;
  hasMedia: boolean;
  gridSplit: { rows: number; cols: number } | null;
  activeFeatureGrid: { rows: number; cols: number } | null;
  selectedCells: Set<string>;
  onSelectedCellsChange: (cells: Set<string>) => void;
  mediaRef: React.RefObject<HTMLImageElement | HTMLVideoElement | HTMLAudioElement | null>;
  isPlayingAudio: boolean;
  setIsPlayingAudio: (v: boolean) => void;
  isPlayingVideo: boolean;
  videoProgress: number;
  videoDuration: number;
  onToggleVideoPlay: () => void;
  onVideoTimeUpdate: () => void;
  onVideoSeek: (time: number) => void;
  onVideoEnded: () => void;
  onVideoLoadedMetadata: () => void;
}> = memo(({ data, hasMedia, gridSplit, activeFeatureGrid, selectedCells, onSelectedCellsChange, mediaRef, isPlayingAudio, setIsPlayingAudio, isPlayingVideo, videoProgress, videoDuration, onToggleVideoPlay, onVideoTimeUpdate, onVideoSeek, onVideoEnded, onVideoLoadedMetadata }) => {
  if (!hasMedia) {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <ImageIcon2 size={48} className="text-white/[0.08]" />
      </div>
    );
  }

  return (
    <>
      {data.data.image && (
        <RetryImage
          ref={mediaRef as any}
          src={data.data.image}
          className="w-full h-full object-cover bg-zinc-900"
          draggable={false}
        />
      )}
      {data.data.videoUri && (
        <div className="relative w-full h-full">
          <video
            key={data.data.videoUri}
            ref={mediaRef as any}
            src={data.data.videoUri}
            className="w-full h-full object-cover bg-zinc-900"
            loop
            playsInline
            preload="metadata"
            onTimeUpdate={onVideoTimeUpdate}
            onEnded={onVideoEnded}
            onLoadedMetadata={onVideoLoadedMetadata}
          />
          <VideoProgressBar
            isPlaying={isPlayingVideo}
            progress={videoProgress}
            duration={videoDuration}
            onTogglePlay={onToggleVideoPlay}
            onSeek={onVideoSeek}
          />
        </div>
      )}
      {data.data.audioUri && (
        <div className="w-full h-full flex flex-col items-center justify-center gap-4 relative">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-amber-900/10 z-0" />
          <audio
            ref={mediaRef as any}
            src={data.data.audioUri}
            onEnded={() => setIsPlayingAudio(false)}
            onPlay={() => setIsPlayingAudio(true)}
            onPause={() => setIsPlayingAudio(false)}
            className="hidden"
          />
        </div>
      )}
      {gridSplit && data.data.image && (
        <GridOverlay
          rows={gridSplit.rows}
          cols={gridSplit.cols}
          interactive
          selectedCells={selectedCells}
          onCellClick={(cellId) => {
            const next = new Set(selectedCells);
            if (next.has(cellId)) { next.delete(cellId); } else { next.add(cellId); }
            onSelectedCellsChange(next);
          }}
        />
      )}
      {activeFeatureGrid && (activeFeatureGrid.rows > 1 || activeFeatureGrid.cols > 1) && !gridSplit && (
        <GridOverlay rows={activeFeatureGrid.rows} cols={activeFeatureGrid.cols} />
      )}
    </>
  );
});
